import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Volume2, Music, Languages, RotateCcw, AlertTriangle } from "lucide-react";
import { useGame } from "@/context/GameContext";
import { sound } from "@/lib/sound";

const Row = ({ icon: I, title, titleKr, children }) => (
  <div className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 p-4">
    <div className="flex items-center gap-3">
      <I className="h-5 w-5 text-white/70" />
      <div>
        <p className="text-sm font-medium text-white">{title}</p>
        <p className="kr text-xs text-white/45">{titleKr}</p>
      </div>
    </div>
    {children}
  </div>
);

export default function SettingsMenu({ open, onOpenChange, currentWorld }) {
  const { state, setSetting, reset } = useGame();
  const { muted, music, koreanSubs } = state.settings;
  const [confirm, setConfirm] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="border-white/10 bg-black/90 text-white backdrop-blur-2xl sm:max-w-md"
        data-testid="settings-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight text-white">
            Settings <span className="kr text-sm font-normal text-white/50">/ 설정</span>
          </DialogTitle>
          <DialogDescription className="sr-only">Audio, language, and progress settings.</DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-3">
          <Row icon={Volume2} title="Sound Effects" titleKr="효과음">
            <Switch
              data-testid="toggle-sfx"
              checked={!muted}
              onCheckedChange={(v) => setSetting("muted", !v)}
            />
          </Row>
          <Row icon={Music} title="Background Music" titleKr="배경 음악">
            <Switch
              data-testid="toggle-music"
              checked={music}
              onCheckedChange={(v) => {
                setSetting("music", v);
                if (v && currentWorld) sound.startMusic(currentWorld);
                else sound.stopMusic();
              }}
            />
          </Row>
          <Row icon={Languages} title="Korean Subtitles" titleKr="한국어 자막">
            <Switch
              data-testid="toggle-korean"
              checked={koreanSubs}
              onCheckedChange={(v) => setSetting("koreanSubs", v)}
            />
          </Row>

          {!confirm ? (
            <button
              onClick={() => setConfirm(true)}
              data-testid="reset-progress"
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300 transition-colors hover:bg-red-500/20"
            >
              <RotateCcw className="h-4 w-4" /> Reset Progress
            </button>
          ) : (
            <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4">
              <p className="flex items-center gap-2 text-sm text-red-200">
                <AlertTriangle className="h-4 w-4" /> Erase all unlocks & memories?
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => setConfirm(false)}
                  className="flex-1 rounded-full border border-white/15 py-2 text-sm text-white/80"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    reset();
                    setConfirm(false);
                    onOpenChange(false);
                  }}
                  data-testid="reset-confirm"
                  className="flex-1 rounded-full bg-red-500 py-2 text-sm font-medium text-white"
                >
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
